import { Component, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { CONFIG_MODAL_INPUTS_TOKEN, ModalOverlayRef } from '@core/classes/modal-overlay.ref';
import {
  Currency,
  DataTypeOptions,
  Employment,
  EmploymentRelationship,
  FuturePlan,
  InvestorProfile,
  Loan,
  MaxContributionYears,
  MinContributionYears
} from '@core/constants/data-types.constants';
import { CurrencyAmount } from '@core/models/currency-amount.model';
import {
  FinancialData,
  InsurancesData,
  LifePlan,
  Person,
  ProfessionalData,
  SimpleInsurance,
  deepCloneLifePlan
} from '@core/models/life-plan.model';
import { ConfigService } from '@core/services/config.service';
import { LifePlanManagerService } from '@core/services/life-plan-manager.service';
import { LiteService } from '@core/services/lite.service';
import { ReportsService } from '@core/services/reports.service';
import { UiService } from '@core/services/ui.service';
import { Store } from '@ngrx/store';
import * as actions from '@redux/actions';
import { AppState } from '@redux/app.reducers';
import {
  CustomValidators,
  isFieldInvalid,
  isFieldValid,
  isOptionalFieldSuccessWhenComplete
} from '@shared/utils/forms/validations.util';
import { calculateGoalYearsLimits } from '@shared/utils/functions.utils';
import { formatISO, parseISO } from 'date-fns';
import { Subscription } from 'rxjs';
import { switchMap, tap, toArray } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';

export type CompleteFormType = 'FINANCIAL_INFORMATION' | 'DEATH' | 'SAVING' | 'RETIREMENT';
@Component({
  selector: 'mfp-complete-form',
  templateUrl: './complete-form.component.html',
  styleUrls: ['./complete-form.component.scss']
})
export class CompleteFormComponent implements OnInit, OnDestroy {
  @Input() type: CompleteFormType = 'FINANCIAL_INFORMATION';

  completeForm: FormGroup;

  isFieldValid = isFieldValid;
  isFieldInvalid = isFieldInvalid;
  isOptionalFieldSuccessWhenComplete = isOptionalFieldSuccessWhenComplete;

  minContributionYears = MinContributionYears;
  maxContributionYears = MaxContributionYears;

  currencyOptions: DataTypeOptions<Currency> = [];
  investorProfileOptions: DataTypeOptions<InvestorProfile> = [];
  futurePlansOptions: DataTypeOptions<FuturePlan> = [];
  loansOptions: DataTypeOptions<Loan> = [];
  employmentRelationship: DataTypeOptions<EmploymentRelationship> = [];
  occupation: DataTypeOptions<Employment> = [];
  goalYearsLimit: any;

  $storeSubscription: Subscription;

  lifePlan!: LifePlan;

  get futurePlansFormArray(): FormArray {
    return this.completeForm.controls.futurePlans as FormArray;
  }

  get loansFormArray(): FormArray {
    return this.completeForm.controls.loans as FormArray;
  }

  constructor(
    public dialogRef: ModalOverlayRef,
    @Optional() @Inject(CONFIG_MODAL_INPUTS_TOKEN) config: any,
    private formBuilder: FormBuilder,
    private store: Store<AppState>,
    private configService: ConfigService,
    private uiService: UiService,
    private lifePlanManager: LifePlanManagerService,
    private reportsService: ReportsService,
    private liteService: LiteService
  ) {
    this.goalYearsLimit = calculateGoalYearsLimits();
    if (config) {
      this.type = config.type;
    }
    this.completeForm = this.formBuilder.group({
      employmentRelationshipClient: [
        {
          value: '',
          disabled: false
        },
        CustomValidators.conditional(
          control => this.type === 'DEATH' || this.type === 'RETIREMENT',
          Validators.required
        )
      ],
      occupationClient: [
        {
          value: '',
          disabled: false
        },
        CustomValidators.conditional(
          control => this.type === 'DEATH' || this.type === 'RETIREMENT',
          Validators.required
        )
      ],
      employmentRelationshipSpouse: [
        {
          value: '',
          disabled: false
        },
        CustomValidators.conditional(
          control => this.hasSpouse() && (this.type === 'DEATH' || this.type === 'RETIREMENT'),
          Validators.required
        )
      ],
      occupationSpouse: [
        {
          value: '',
          disabled: false
        },
        CustomValidators.conditional(
          control => this.hasSpouse() && (this.type === 'DEATH' || this.type === 'RETIREMENT'),
          Validators.required
        )
      ],
      yearsWorkedSpouse: [
        {
          value: '',
          disabled: false
        },
        CustomValidators.conditional(
          control => this.hasSpouse() && (this.type === 'DEATH' || this.type === 'RETIREMENT'),
          Validators.compose([
            Validators.required,
            Validators.max(MaxContributionYears),
            Validators.min(MinContributionYears)
          ])
        )
      ],
      spouseNetMonthlyIncome: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(
          control =>
            this.hasSpouse() &&
            (this.type === 'FINANCIAL_INFORMATION' || this.type === 'DEATH' || this.type === 'SAVING'),
          Validators.required
        )
      ],
      investorProfile: [
        '',
        CustomValidators.conditional(
          control => this.type === 'SAVING' || this.type === 'RETIREMENT',
          Validators.required
        )
      ],
      hasFuturePlans: [false],
      futurePlans: new FormArray([]),
      hasLoans: [false],
      loans: new FormArray([]),
      hasDeathInsurance: [false],
      deathInsuranceAssuredAmount: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(
          control => this.completeForm?.controls.hasDeathInsurance.value,
          CustomValidators.requiredComplete
        )
      ],
      deathInsuranceMonthlyFee: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(this.ifAllInnerFieldsSet, CustomValidators.requiredComplete)
      ],
      hasRetirementInsurance: [false],
      retirementInsuranceAssuredAmount: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(
          control => this.completeForm?.controls.hasRetirementInsurance.value,
          CustomValidators.requiredComplete
        )
      ],
      retirementInsuranceMonthlyFee: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(this.ifAllInnerFieldsSet, CustomValidators.requiredComplete)
      ],
      hasSpouseDeathInsurance: [false],
      spouseDeathInsuranceAssuredAmount: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(
          control => this.completeForm?.controls.hasSpouseDeathInsurance.value,
          CustomValidators.requiredComplete
        )
      ],
      spouseDeathInsuranceMonthlyFee: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(this.ifAllInnerFieldsSet, CustomValidators.requiredComplete)
      ],
      hasSpouseRetirementInsurance: [false],
      spouseRetirementInsuranceAssuredAmount: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(
          control => this.completeForm?.controls.hasSpouseRetirementInsurance.value,
          CustomValidators.requiredComplete
        )
      ],
      spouseRetirementInsuranceMonthlyFee: [
        this.configService.getDefaultCurrencyAmount(),
        CustomValidators.conditional(this.ifAllInnerFieldsSet, CustomValidators.requiredComplete)
      ]
    });

    this.$storeSubscription = this.store.subscribe({
      next: state => {
        this.currencyOptions = state.config.currencies;
        this.investorProfileOptions = state.config.investorProfiles;
        this.futurePlansOptions = state.config.futurePlans;
        this.loansOptions = state.config.loans;
        this.setFinancialValuesFromLifePlan(state.lifePlan.currentLifePlan);
        this.employmentRelationship = state.config.employmentRelationships;
        this.occupation = state.config.employments;
        this.setProfessionalValuesFromLifePlan(state.lifePlan.currentLifePlan);
        this.setInsurancesValuesFromLifePlan(state.lifePlan.currentLifePlan);
      }
    });
  }

  close() {
    this.dialogRef.close();
  }

  save() {
    this.completeForm.markAllAsTouched();
    if (this.completeForm.valid) {
      this.uiService.presentLoading();
      this.store.dispatch(
        actions.completedCompleteFormEdit({
          user: this.getUserData(),
          spouse: this.getSpouseData()
        })
      );
      this.reportsService
        .getAndUpdateReportsData(this.lifePlan)
        .pipe(
          switchMap(() =>
            this.liteService
              .calculateAllTargetsWithLimitsFromLifePlan(deepCloneLifePlan(this.lifePlan) as LifePlan)
              .pipe(
                toArray(),
                tap(responses => {
                  // FIXME: this actions are executed asynchronously so there is a chance that the call to the
                  // lifePlanManager.updateAndManageLifePlan is performed with a wrong lifePlan
                  responses.forEach(response => {
                    this.store.dispatch(
                      actions.updateLifePlanInsuranceTargetCurrentProductWithLiteValuesAndLimits({
                        insuranceTargetId: response.insuranceTarget.id,
                        productId: response.insuranceTarget.currentProduct?.id || '',
                        liteResponses: response.liteResponses
                      })
                    );
                  });
                })
              )
          ),
          switchMap(() => this.lifePlanManager.updateAndManageLifePlan(this.lifePlan)),
          switchMap(lifePlan =>
            this.lifePlanManager.generateLifePlanInsuranceTargetsAndPopulateThemWithProducts(lifePlan, 'all')
          ),
          tap(lifePlan => this.store.dispatch(actions.forceUpdateDefaultLifePlan({ defaultLifePlan: lifePlan })))
        )
        .subscribe({
          next: lifePlan => {
            this.dialogRef.close();
          },
          error: error => {
            this.uiService.dismissLoading();
            this.uiService.openErrorModal();
          },
          complete: () => this.uiService.dismissLoading()
        });
    }
  }

  ngOnInit(): void {
    // Future plans
    this.completeForm.controls.hasFuturePlans.valueChanges.subscribe({
      next: v => {
        const formArray = this.futurePlansFormArray;
        if (v) {
          if (formArray.length === 0) {
            formArray.push(this.createFuturePlansDefaultValue());
          }
        } else {
          formArray.clear();
        }
      }
    });
    this.completeForm.controls.futurePlans.valueChanges.subscribe({
      next: v => {
        if (v.length === 0) {
          this.completeForm.patchValue({
            hasFuturePlans: false
          });
        } else {
          this.completeForm.patchValue({
            hasFuturePlans: true
          });
        }
      }
    });
    // Loans
    this.completeForm.controls.hasLoans.valueChanges.subscribe({
      next: v => {
        const formArray = this.loansFormArray;
        if (v) {
          if (formArray.length === 0) {
            formArray.push(this.createLoansDefaultValue());
          }
        } else {
          formArray.clear();
        }
      }
    });
    this.completeForm.controls.loans.valueChanges.subscribe({
      next: v => {
        if (v.length === 0) {
          this.completeForm.patchValue({
            hasLoans: false
          });
        } else {
          this.completeForm.patchValue({
            hasLoans: true
          });
        }
      }
    });
    this.completeForm.controls.hasDeathInsurance.valueChanges.subscribe(value => {
      this.completeForm.controls.deathInsuranceAssuredAmount.reset(this.configService.getDefaultCurrencyAmount());
      this.completeForm.controls.deathInsuranceMonthlyFee.reset(this.configService.getDefaultCurrencyAmount());
    });
    this.completeForm.controls.hasSpouseDeathInsurance.valueChanges.subscribe(value => {
      this.completeForm.controls.spouseDeathInsuranceAssuredAmount.reset(this.configService.getDefaultCurrencyAmount());
      this.completeForm.controls.spouseDeathInsuranceMonthlyFee.reset(this.configService.getDefaultCurrencyAmount());
    });
  }

  ngOnDestroy(): void {
    this.$storeSubscription.unsubscribe();
  }

  createFuturePlansDefaultValue = () => {
    return new FormGroup({
      id: new FormControl(''),
      goalAmount: new FormControl(this.configService.getDefaultCurrencyAmount(), CustomValidators.requiredComplete),
      assuredAmount: new FormControl(this.configService.getDefaultCurrencyAmount(), CustomValidators.requiredComplete),
      type: new FormControl('', Validators.required),
      goalDate: new FormControl('', [
        Validators.required,
        Validators.min(this.goalYearsLimit.min),
        Validators.max(this.goalYearsLimit.max)
      ])
    });
  };

  createLoansDefaultValue = () => {
    return new FormGroup({
      pendingAmount: new FormControl(this.configService.getDefaultCurrencyAmount(), CustomValidators.requiredComplete),
      monthlyFee: new FormControl(this.configService.getDefaultCurrencyAmount(), CustomValidators.requiredComplete),
      type: new FormControl('', Validators.required),
      remainingYears: new FormControl('', Validators.required)
    });
  };

  private setFinancialValuesFromLifePlan(lifePlan: LifePlan) {
    this.lifePlan = lifePlan;
    if (lifePlan.user?.financial) {
      const financialData = lifePlan.user.financial;
      if (lifePlan.spouse?.financial) {
        if (financialData.netMonthlyIncome) {
          this.completeForm.controls.spouseNetMonthlyIncome.setValue(lifePlan.spouse.financial.netMonthlyIncome);
        }
      }
      // Investor profile
      this.completeForm.controls.investorProfile.setValue(financialData.investorProfile);
      // Future plans
      this.futurePlansFormArray.clear();
      if (financialData.futurePlans && financialData.futurePlans.length > 0) {
        financialData.futurePlans
          ?.map(fp => {
            const relatedForm = this.createFuturePlansDefaultValue();
            relatedForm.controls.id.setValue(fp.id);
            relatedForm.controls.type.setValue(fp.type);
            relatedForm.controls.goalAmount.setValue(fp.goalAmount);
            relatedForm.controls.assuredAmount.setValue(fp.assuredAmount);
            relatedForm.controls.goalDate.setValue(parseISO(fp.goalDate).getFullYear());
            return relatedForm;
          })
          .forEach(rf => this.futurePlansFormArray.push(rf));
      }
      // Loans
      this.loansFormArray.clear();
      if (financialData.loans && financialData.loans.length > 0) {
        financialData.loans
          ?.map(l => {
            const relatedForm = this.createLoansDefaultValue();
            relatedForm.controls.type.setValue(l.type);
            relatedForm.controls.pendingAmount.setValue(l.pendingAmount);
            relatedForm.controls.monthlyFee.setValue(l.monthlyFee);
            relatedForm.controls.remainingYears.setValue(l.remainingYears);
            return relatedForm;
          })
          .forEach(rf => this.loansFormArray.push(rf));
      }
    }
  }

  private getFinancialData(): any {
    let futurePlans;
    if (this.type === 'SAVING' || this.type === 'RETIREMENT') {
      futurePlans = this.completeForm.controls.futurePlans.value as any[];
      futurePlans.forEach(fp => {
        fp.id = fp.id || uuidv4();
        fp.goalDate = formatISO(new Date(fp.goalDate, 0, 1), { representation: 'date' });
      });
      futurePlans = futurePlans && futurePlans.length > 0 ? futurePlans : [];
    } else {
      futurePlans = this.lifePlan.user?.financial?.futurePlans;
    }

    let loans;
    if (this.type === 'FINANCIAL_INFORMATION' || this.type === 'DEATH') {
      loans = this.completeForm.controls.loans.value as {
        type: string;
        pendingAmount: CurrencyAmount;
        monthlyFee: CurrencyAmount;
        remainingYears: number;
      }[];
      loans = loans && loans.length > 0 ? loans : [];
      loans.forEach(l => (l.remainingYears = +l.remainingYears));
    } else {
      loans = this.lifePlan.user?.financial?.loans;
    }

    return {
      ...this.lifePlan?.user?.financial,
      investorProfile: this.completeForm.controls.investorProfile.value,
      futurePlans,
      loans
    };
  }

  private getSpouseFinancialData(): FinancialData {
    return {
      netMonthlyIncome: this.completeForm.controls.spouseNetMonthlyIncome.value
    };
  }

  getUsername(): string {
    return this.lifePlan?.user?.personal?.name || '';
  }

  hasSpouse(): boolean {
    return !!this.lifePlan?.spouse;
  }

  private ifAllInnerFieldsSet(control: AbstractControl): boolean {
    return CustomValidators.requiredComplete(control) === null;
  }

  private setProfessionalValuesFromLifePlan(lifePlan: LifePlan) {
    this.lifePlan = lifePlan;
    if (lifePlan.user?.professional) {
      const professionalData = lifePlan.user.professional;
      this.completeForm.controls.employmentRelationshipClient.setValue(professionalData.employmentRelationship);
      this.completeForm.controls.occupationClient.setValue(professionalData.employment);
    }

    if (lifePlan.spouse?.professional) {
      const professionalData = lifePlan.spouse.professional;
      this.completeForm.controls.employmentRelationshipSpouse.setValue(professionalData.employmentRelationship);
      this.completeForm.controls.occupationSpouse.setValue(professionalData.employment);
      this.completeForm.controls.yearsWorkedSpouse.setValue(professionalData.contributionYears);
    }
  }

  private getProfessionalData(): ProfessionalData | undefined {
    let professionalData: ProfessionalData | undefined;
    if (this.type === 'DEATH' || this.type === 'RETIREMENT') {
      professionalData = {
        ...this.lifePlan?.user?.professional,
        employmentRelationship: this.completeForm.controls.employmentRelationshipClient.value,
        employment: this.completeForm.controls.occupationClient.value
      };
    } else {
      professionalData = this.lifePlan.user?.professional;
    }
    return professionalData;
  }

  private getSpouseProfessionalData(): ProfessionalData | undefined {
    if (!this.hasSpouse()) {
      return undefined;
    }
    let professionalData: ProfessionalData | undefined;
    if (this.type === 'DEATH' || this.type === 'RETIREMENT') {
      professionalData = {
        employmentRelationship: this.completeForm.controls.employmentRelationshipSpouse.value,
        employment: this.completeForm.controls.occupationSpouse.value,
        contributionYears: +this.completeForm.controls.yearsWorkedSpouse.value
      };
    } else {
      professionalData = this.lifePlan.spouse?.professional;
    }
    return professionalData;
  }

  private setInsurancesValuesFromLifePlan(lifePlan: LifePlan) {
    this.lifePlan = lifePlan;
    if (lifePlan.user?.insurances) {
      const deathInsurance = lifePlan.user.insurances.currentInsurances?.find(i => i.type === 'DEATH');

      if (deathInsurance) {
        this.completeForm.controls.hasDeathInsurance.setValue(true);
        this.completeForm.controls.deathInsuranceAssuredAmount.setValue(deathInsurance.assuredAmount);
        this.completeForm.controls.deathInsuranceMonthlyFee.setValue(deathInsurance.monthlyFee);
      }
    }

    if (lifePlan.spouse?.insurances) {
      const deathInsurance = lifePlan.spouse.insurances.currentInsurances?.find(i => i.type === 'DEATH');

      if (deathInsurance) {
        this.completeForm.controls.hasSpouseDeathInsurance.setValue(true);
        this.completeForm.controls.spouseDeathInsuranceAssuredAmount.setValue(deathInsurance.assuredAmount);
        this.completeForm.controls.spouseDeathInsuranceMonthlyFee.setValue(deathInsurance.monthlyFee);
      }
    }
  }

  private getInsurancesData(): InsurancesData | undefined {
    let insurancesData: InsurancesData | undefined;
    if (this.type === 'DEATH' || this.type === 'RETIREMENT') {
      const currentInsurances: SimpleInsurance[] = [];
      if (this.lifePlan?.user?.insurances?.currentInsurances) {
        currentInsurances.push(...this.lifePlan?.user?.insurances?.currentInsurances);
      }
      if (this.completeForm.controls.hasDeathInsurance.value) {
        currentInsurances.push({
          type: 'DEATH',
          assuredAmount: this.completeForm.controls.deathInsuranceAssuredAmount.value,
          monthlyFee: this.completeForm.controls.deathInsuranceMonthlyFee.value
        });
      }
      insurancesData = { currentInsurances };
    } else {
      insurancesData = this.lifePlan.user?.insurances;
    }
    return insurancesData;
  }

  private getSpouseInsurancesData(): InsurancesData | undefined {
    if (!this.hasSpouse()) {
      return undefined;
    }
    let insurancesData: InsurancesData | undefined;
    if (this.type === 'DEATH' || this.type === 'RETIREMENT') {
      const currentInsurances: SimpleInsurance[] = [];
      if (this.lifePlan?.spouse?.insurances?.currentInsurances) {
        currentInsurances.push(...this.lifePlan?.spouse?.insurances?.currentInsurances);
      }
      if (this.completeForm.controls.hasSpouseDeathInsurance.value) {
        currentInsurances.push({
          type: 'DEATH',
          assuredAmount: this.completeForm.controls.spouseDeathInsuranceAssuredAmount.value,
          monthlyFee: this.completeForm.controls.spouseDeathInsuranceMonthlyFee.value
        });
      }
      insurancesData = { currentInsurances };
    } else {
      insurancesData = this.lifePlan.spouse?.insurances;
    }
    return insurancesData;
  }

  private getUserData(): Person {
    const personal = this.lifePlan?.user?.personal;
    return {
      personal,
      professional: this.getProfessionalData(),
      financial: this.getFinancialData(),
      insurances: this.getInsurancesData()
    };
  }

  private getSpouseData(): Person | undefined {
    if (!this.hasSpouse()) {
      return undefined;
    }
    const personal = this.lifePlan?.spouse?.personal;
    return {
      personal,
      professional: this.getSpouseProfessionalData(),
      financial: this.getSpouseFinancialData(),
      insurances: this.getSpouseInsurancesData()
    };
  }
}
